import { useRef, useState } from 'react';
import { Camera, Image as ImageIcon, Loader2, Send, X, Newspaper, Calendar, Building2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const SHARE_TYPE_META = {
  property: { icon: Building2, label: 'Mi propiedad' },
  news: { icon: Newspaper, label: 'Noticia' },
  event: { icon: Calendar, label: 'Evento' },
};

export default function FeedComposer({ user, onCreated }) {
  const { user: authUser } = useAuth();
  const currentUser = user || authUser;
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [sharePickerType, setSharePickerType] = useState(null); // 'property'|'news'|'event'
  const [shareItems, setShareItems] = useState([]);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareSelection, setShareSelection] = useState(null); // {type, id, title, image, subtitle}
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        if (!file.type?.startsWith('image/')) continue;
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (data?.url) uploaded.push(data.url);
      }
      setImages((prev) => [...prev, ...uploaded].slice(0, 4));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir la foto');
    } finally {
      setUploading(false);
    }
  };

  const openSharePicker = async (type) => {
    setSharePickerType(type);
    setShareLoading(true);
    try {
      let path;
      if (type === 'property') path = '/properties/mine';
      else if (type === 'news') path = '/news';
      else if (type === 'event') path = '/events';
      const { data } = await api.get(path);
      setShareItems(Array.isArray(data) ? data : []);
    } catch {
      setShareItems([]);
    } finally {
      setShareLoading(false);
    }
  };

  const chooseShareItem = (item) => {
    const type = sharePickerType;
    let selection;
    if (type === 'property') {
      selection = {
        type, id: item.property_id, title: item.title,
        image: (item.images || [])[0],
        subtitle: `${item.city || ''} · US$ ${Number(item.price_usd || 0).toLocaleString('es-BO')}`,
      };
    } else if (type === 'news') {
      selection = { type, id: item.slug, title: item.title, image: item.image_url, subtitle: item.category || 'Noticia' };
    } else {
      selection = { type, id: item.slug, title: item.title, image: item.cover_image, subtitle: `${item.city || ''} · ${String(item.starts_at || '').slice(0, 10)}` };
    }
    setShareSelection(selection);
    setSharePickerType(null);
    setShowAttach(false);
  };

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed && images.length === 0 && !shareSelection) return;
    setPosting(true);
    try {
      const body = {
        text: trimmed,
        images,
        share_type: shareSelection?.type || null,
        share_ref_id: shareSelection?.id || null,
      };
      const { data } = await api.post('/community/posts', body);
      onCreated?.(data);
      setText('');
      setImages([]);
      setShareSelection(null);
      setShowAttach(false);
      toast.success('Publicación compartida');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo publicar');
    } finally {
      setPosting(false);
    }
  };

  const avatarChar = user?.name?.charAt(0) || 'S';

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="feed-composer">
      <div className="flex items-start gap-3 p-4">
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cnib-blue font-heading text-sm font-bold text-white">
            {avatarChar}
          </span>
        )}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder={`¿Qué quieres compartir, ${user?.name?.split(' ')[0] || 'colega'}?`}
          className="min-h-[52px] flex-1 resize-none rounded-xl border-0 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:ring-2 focus:ring-cnib-blue/15"
          data-testid="feed-composer-input"
        />
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-2 gap-2 px-4 pb-2 sm:grid-cols-4" data-testid="feed-composer-images">
          {images.map((url, i) => (
            <div key={url + i} className="relative aspect-square overflow-hidden rounded-xl">
              <img src={url} alt="" className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                className="absolute right-1 top-1 rounded-full bg-slate-900/70 p-1 text-white"
                data-testid={`feed-composer-remove-img-${i}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {shareSelection && (
        <div className="mx-4 mb-2 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-2.5" data-testid="feed-composer-share">
          {shareSelection.image && (
            <img src={shareSelection.image} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-orange-500">
              {SHARE_TYPE_META[shareSelection.type]?.label}
            </p>
            <p className="truncate text-sm font-bold text-slate-900">{shareSelection.title}</p>
            <p className="truncate text-xs text-slate-500">{shareSelection.subtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => setShareSelection(null)}
            className="rounded-full p-1 text-slate-400 hover:bg-slate-200"
            data-testid="feed-composer-share-remove"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2.5">
        <div className="flex items-center gap-1">
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => uploadFiles(Array.from(e.target.files || []))} />
          <input ref={galleryRef} type="file" accept="image/*" multiple hidden onChange={(e) => uploadFiles(Array.from(e.target.files || []))} />
          <button
            type="button"
            onClick={() => cameraRef.current?.click()}
            disabled={uploading || images.length >= 4}
            className="flex items-center gap-1 rounded-full px-3 py-2 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-40"
            data-testid="feed-composer-camera"
          >
            <Camera className="h-4 w-4" />
            <span className="hidden sm:inline">Foto</span>
          </button>
          <button
            type="button"
            onClick={() => galleryRef.current?.click()}
            disabled={uploading || images.length >= 4}
            className="flex items-center gap-1 rounded-full px-3 py-2 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-40"
            data-testid="feed-composer-gallery"
          >
            <ImageIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Galería</span>
          </button>
          <button
            type="button"
            onClick={() => setShowAttach((v) => !v)}
            className="flex items-center gap-1 rounded-full px-3 py-2 text-xs font-bold text-slate-600 transition-colors hover:bg-slate-100"
            data-testid="feed-composer-attach"
          >
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Mi propiedad</span>
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={posting || (!text.trim() && images.length === 0 && !shareSelection)}
          data-testid="feed-composer-submit"
          className="flex items-center gap-2 rounded-full bg-orange-500 px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:opacity-50"
        >
          {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Publicar
        </button>
      </div>

      {showAttach && !sharePickerType && (
        <div className="grid grid-cols-3 gap-2 border-t border-slate-100 p-3" data-testid="feed-composer-attach-menu">
          {Object.entries(SHARE_TYPE_META)
            .filter(([key]) => isAdmin || key === 'property')
            .map(([key, meta]) => (
            <button
              key={key}
              type="button"
              onClick={() => openSharePicker(key)}
              data-testid={`feed-composer-attach-${key}`}
              className="flex flex-col items-center gap-1 rounded-xl bg-slate-50 py-3 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-100"
            >
              <meta.icon className="h-5 w-5 text-cnib-blue" />
              {meta.label}
            </button>
          ))}
        </div>
      )}

      {sharePickerType && (
        <div className="border-t border-slate-100 p-3" data-testid="feed-composer-share-picker">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Elige {SHARE_TYPE_META[sharePickerType]?.label.toLowerCase()}
            </p>
            <button
              type="button"
              onClick={() => setSharePickerType(null)}
              className="rounded-full p-1 text-slate-400 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {shareLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-cnib-blue" />
            </div>
          ) : shareItems.length === 0 ? (
            <p className="py-4 text-center text-xs text-slate-500">No hay contenido disponible para compartir.</p>
          ) : (
            <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
              {shareItems.slice(0, 20).map((it) => {
                const img = sharePickerType === 'property' ? (it.images || [])[0] : (it.cover_image || it.image_url);
                const title = it.title;
                const sub = sharePickerType === 'property'
                  ? `US$ ${Number(it.price_usd || 0).toLocaleString('es-BO')}`
                  : sharePickerType === 'news' ? it.category : `${it.city || ''}`;
                return (
                  <button
                    key={it.property_id || it.slug || it.event_id}
                    type="button"
                    onClick={() => chooseShareItem(it)}
                    data-testid={`feed-composer-share-pick-${it.property_id || it.slug}`}
                    className="flex w-full items-center gap-2 rounded-lg p-2 text-left transition-colors hover:bg-slate-50"
                  >
                    {img ? <img src={img} alt="" className="h-10 w-10 shrink-0 rounded-lg object-cover" /> : <span className="h-10 w-10 rounded-lg bg-slate-200" />}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-bold text-slate-900">{title}</p>
                      <p className="truncate text-[11px] text-slate-500">{sub}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
